import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Header from '@/components/navigation/Header'
import Sidebar from '@/components/navigation/Sidebar'
import MobileNavigation from '@/components/navigation/MobileNavigation'
import { selectSidebarOpen } from '@/store/slices/uiSlice'
import { useGetAlertsQuery, useGetWorkOrdersQuery } from '@/store/api'

interface MainLayoutProps {
  children: React.ReactNode
}

export default function MainLayout({ children }: MainLayoutProps) {
  const location = useLocation()
  const sidebarOpen = useSelector(selectSidebarOpen)
  
  // Fetch real-time counts from API (same logic as Sidebar)
  const { data: alertsData } = useGetAlertsQuery({ 
    limit: 100,
    sortBy: 'created_at', // Use snake_case for database column
    sortOrder: 'desc' 
  })
  
  const { data: workOrdersData } = useGetWorkOrdersQuery({ 
    limit: 100,
    status: ['pending', 'assigned', 'in-progress'], // Use hyphen format (backend normalizes)
    sortBy: 'created_at', // Use snake_case for database column
    sortOrder: 'desc' 
  })
  
  // Calculate real counts (same logic as Sidebar)
  const alerts = alertsData?.data || []
  const workOrders = workOrdersData?.data || []
  
  const alertCount = alerts.filter(alert => 
    alert.status === 'new' || alert.priority === 'critical'
  ).length
  
  const workOrderCount = workOrders.filter(wo => {
    // Count all active work orders
    return wo.status === 'pending' || wo.status === 'assigned' || wo.status === 'in-progress'
  }).length

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main content area */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'
      }`}>
        {/* Header */}
        <Header />
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="container-predictive py-6 pb-20 md:pb-6">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation 
        currentPath={location.pathname}
        alertCount={alertCount}
        workOrderCount={workOrderCount}
      />
    </div>
  )
}